import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage as ErrorMessageContainer } from "@hookform/error-message";
import errorSchemaMessages from 'venuescanner-js/react/components/Form/copy/errors';
import { PaymentCapabilities } from 'venuescanner-js/constants/venue';
import {
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Tip from 'venuescanner-js/react/components/Inbox/components/Objects/Tip';
import EnquiryCtas from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryCtas/index.js';
import EnquiryTitle from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryTitle/index.js';
import './quote-form.scss';

const requiredMessage = 'Please enter an amount';

const schema = yup.object().shape({
  price: yup
    .number()
    .typeError(errorSchemaMessages.default.number)
    .required(requiredMessage)
    .min(1, requiredMessage),
  vatIncluded: yup.boolean().required(requiredMessage),
});

export function PriceForm({
  onSubmit,
  onCancel,
  defaultValues,
  paymentCapabilities,
  currency,
}) {
  const {
    formState: { errors },
    handleSubmit,
    register,
    control,
    watch,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const watchOfflinePayment = watch('offlinePayment');

  const collectionCopy = watchOfflinePayment
    ? 'You will be responsible for collecting payment from the organiser'
    : 'The organiser will pay this amount via VenueScanner to confirm their booking';

  return (
    <>
      <form
        id='quoteForm'
        name='quoteForm'
        className='enquiry quote-form'
        onSubmit={handleSubmit(onSubmit)}>
        <EnquiryTitle title='Quote' onCancel={onCancel} />

        <Stack className='enquiry-info' spacing={2} direction='column'>
          <Typography variant='body1' sx={{ fontSize: 16, color: '#000' }}>
            Total event price
          </Typography>
          <PriceInput
            register={register}
            control={control}
            currency={currency}
            paymentCapabilities={paymentCapabilities}
            errors={errors}
          />
          <Typography sx={{ fontSize: 13, color: '#000', lineHeight: 1.3 }}>
            {collectionCopy}
          </Typography>
        </Stack>
        {!watchOfflinePayment && <VAT register={register} control={control} />}
        {paymentCapabilities ===
          PaymentCapabilities.ONLINE_SUBSCRIPTION_PAYMENT && (
          <div className='offline-payment'>
            <div className='offline-payment__input-wrapper'>
              <input
                id='offline-payment'
                type='checkbox'
                {...register('offlinePayment')}
              />
              <label htmlFor='offline-payment'>
                I don&apos;t want to collect payment via VenueScanner for this
                event
              </label>
            </div>
          </div>
        )}
      </form>
      <EnquiryCtas sx={{ pl: 1.5, pr: 1.5 }}>
        <Button
          fullWidth
          form='quoteForm'
          color='primary'
          variant='contained'
          size='medium-large'
          type='submit'>
          Next
        </Button>
        <Button
          size='medium-large'
          fullWidth
          variant='text'
          color='primary'
          onClick={onCancel}>
          Cancel
        </Button>
        <Tip
          title='Tip'
          text='When you mark an enquiry as booked your venue gets a higher search ranking'
        />
      </EnquiryCtas>
    </>
  );
}

function PriceInput({
  errors,
  control,
  register,
  paymentCapabilities,
  currency,
}) {
  const offlinePayment = useWatch({ control, name: 'offlinePayment' });

  return (
    <Stack direction='column' spacing={2}>
      <TextField
        size='small'
        variant='outlined'
        sx={{
          '& .MuiInputBase-root': {
            paddingLeft: 1.25,
            fontSize: 14,
            background: '#fff',
            mb: 0.5,
          },
          '& .MuiInputBase-input': { color: '#000' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 1,
            top: -10,
            borderColor: '#7E8CAB',
            borderWidth: '0.5px',
          },
        }}
        InputProps={{
          id: 'price',
          type: 'number',
          startAdornment: (
            <InputAdornment sx={{ color: '#000' }} position='start'>
              {currency.symbol}
            </InputAdornment>
          ),
          ...register('price', {
            min: { value: 1, message: 'Please enter an amount' },
          }),
        }}
      />
      {paymentCapabilities ===
        PaymentCapabilities.ONLINE_SUBSCRIPTION_PAYMENT &&
        !offlinePayment && (
          <small>Payment handling fee will be 2% of the payment amount</small>
        )}
      <ErrorMessageContainer errors={errors} name='price' as={ErrorMessage} />
    </Stack>
  );
}

function VAT({ register, control }) {
  const vatIncluded = useWatch({ control, name: 'vatIncluded' });

  return (
    <>
      <div className='quote-form__vat'>
        <label htmlFor='vat'>Does this figure include VAT?</label>
        <input id='vat' type='checkbox' {...register('vatIncluded')} />
      </div>
      {!vatIncluded && (
        <div className='vat-exempt'>
          <small>We will add 20% VAT to the total quote amount</small>
          <div className='vat-exempt__input-wrapper'>
            <input
              id='vat-exempt-input'
              type='checkbox'
              {...register('vatExempt')}
            />
            <label htmlFor='vat-exempt-input'>VAT exempt</label>
          </div>
        </div>
      )}
    </>
  );
}

function ErrorMessage({ children }) {
  return <p className='error-message'>{children}</p>;
}
